import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    Stack,
} from "@mui/material";

// Custom icons imported from Figma design
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { FoodsIcon } from "@foodpilot/foods";

import { useTranslation } from "react-i18next";
import { ProductLineForm, useGetAllProductLines } from "src/api/client-api/productLines/useProductLines";
import { Loading } from "src/components/utils/Loading";
import {
    useDeleteProductLine,
    extractProductLineDeleteImpact,
    type ProductLineDeleteImpact,
} from "src/api/client-api/productLines/useDeleteProductLine";
import { ProductLineService } from "./Services/ProductLineService";
import { CREATE, PRODUCT_LINE, useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useNavigate } from "react-router";
import { ProductLineModal } from "./ProductLineModal";
import { useQueryClient } from "react-query";
import { useSnackbar } from "src/utils/useSnackbar";
import { SettingsError } from "./SettingsError";
import { DeleteProductLineDialog, type DeleteProductLineStep } from "./ProductLine/DeleteProductLineDialog";

type ProductLineWithLevel = ProductLineForm & {
    level: number;
    parentId?: number;
};

export const ProductLineList = () => {
    const { canUser } = useFoodpilotContext();
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const { productLines, isLoading, isError, error } = useGetAllProductLines();
    const deleteProductLine = useDeleteProductLine();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();

    const [productLinesForm, setProductLinesForm] = useState<ProductLineForm[] | undefined>(undefined);

    // Single dialog drives both deletion steps:
    //   - "confirm": initial "are you sure?" prompt, no impact info yet.
    //   - "in-use": escalated when the API returned 409 because the line
    //               still has sub-lines and/or products. Confirming retries
    //               with `?force=true` to cascade the deletion.
    const [deleteDialog, setDeleteDialog] = useState<{
        open: boolean;
        productLineId: number | null;
        productLineName: string;
        step: DeleteProductLineStep;
        impact: ProductLineDeleteImpact;
    }>({
        open: false,
        productLineId: null,
        productLineName: "",
        step: "confirm",
        impact: { descendantsCount: 0, directProductsCount: 0, totalProductsCount: 0 },
    });

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProductLine, setEditingProductLine] = useState<ProductLineForm | undefined>(undefined);

    useEffect(() => {
        if (!productLines) return;
        setProductLinesForm(ProductLineService.getProductLinesToProductLineForms(productLines));
    }, [productLines]);

    // Handle loading state
    if (isLoading) return <Loading />;

    // Handle error state (especially authentication errors)
    if (isError) {
        return (
            <SettingsError
                heading={t("gammes", "Product Lines")}
                message={t(
                    "productLine.error.loadFailed",
                    "Failed to load product lines. Please try refreshing the page or contact support if the problem persists.",
                )}
            />
        );
    }

    const handleDeleteClick = (productLine: ProductLineForm) => {
        if (!productLine.id) return;
        setDeleteDialog({
            open: true,
            productLineId: productLine.id,
            productLineName: productLine.name,
            step: "confirm",
            impact: { descendantsCount: 0, directProductsCount: 0, totalProductsCount: 0 },
        });
    };

    const handleDeleteCancel = () => {
        setDeleteDialog((prev) => ({ ...prev, open: false }));
    };

    const performDelete = (productLineId: number, force: boolean) => {
        deleteProductLine.mutate(
            { id: productLineId, force },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["allProductLines"] });
                    setDeleteDialog((prev) => ({ ...prev, open: false }));
                    snackbar.setSnackbarProps({
                        message: t("productLine.deleteSuccess", "Product line deleted successfully"),
                        type: "success",
                    });
                },
                onError: (error: unknown) => {
                    const apiError = error as {
                        response?: { status?: number; data?: { detail?: string; "hydra:description"?: string } };
                    };

                    // 409 only fires on the "confirm" step: the line still has
                    // sub-lines and/or products. Escalate to the impact step so
                    // the user can confirm a destructive force delete that
                    // cascades over the whole subtree.
                    if (!force && apiError?.response?.status === 409) {
                        const message = apiError.response.data?.detail ?? apiError.response.data?.["hydra:description"];
                        const impact = extractProductLineDeleteImpact(message);
                        setDeleteDialog((prev) =>
                            prev.productLineId === null ? prev : { ...prev, step: "in-use", impact },
                        );
                        return;
                    }

                    snackbar.setSnackbarProps({
                        message: t("productLine.errorOnDelete", "Error deleting product line"),
                        type: "error",
                    });
                    setDeleteDialog((prev) => ({ ...prev, open: false }));
                },
            },
        );
    };

    const handleDeleteConfirm = () => {
        if (deleteDialog.productLineId === null) return;
        const force = deleteDialog.step === "in-use";
        performDelete(deleteDialog.productLineId, force);
    };

    // Helper function to find a product line in the hierarchy
    const findProductLineInHierarchy = (productLines: ProductLineForm[], id: number): ProductLineForm | null => {
        for (const productLine of productLines) {
            if (productLine.id === id) return productLine;
        }
        return null;
    };

    // Helper function to check if a product line has children
    const hasChildren = (productLineId: number): boolean => {
        if (!productLinesForm) return false;
        return productLinesForm.some((pl) => pl.parent?.id === productLineId);
    };

    // Helper function to get children of a product line
    const getChildren = (productLineId: number): ProductLineForm[] => {
        if (!productLinesForm) return [];
        return productLinesForm.filter((pl) => pl.parent?.id === productLineId);
    };

    // Helper function to get only root product lines (products that are not children of others)
    const getRootProductLines = (allProductLines: ProductLineForm[]): ProductLineForm[] => {
        return allProductLines.filter((productLine) => !productLine.parent);
    };

    // Helper function to flatten hierarchy for display
    const flattenProductLinesForDisplay = (productLines: ProductLineForm[], level = 0): ProductLineWithLevel[] => {
        const result: ProductLineWithLevel[] = [];

        productLines.forEach((productLine) => {
            result.push({ ...productLine, level });

            // If expanded and has children, add them recursively
            if (expandedRows.has(productLine.id!) && hasChildren(productLine.id!)) {
                const children = getChildren(productLine.id!);
                const flattenedChildren = flattenProductLinesForDisplay(children, level + 1).map((child) => ({
                    ...child,
                    parentId: productLine.id,
                }));
                result.push(...flattenedChildren);
            }
        });

        return result;
    };

    const toggleRowExpansion = (id: number) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(id)) {
            newExpandedRows.delete(id);
        } else {
            newExpandedRows.add(id);
        }
        setExpandedRows(newExpandedRows);
    };

    // Modal handlers
    const handleOpenCreateModal = () => {
        setEditingProductLine(undefined);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (productLine: ProductLineForm) => {
        setEditingProductLine(productLine);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingProductLine(undefined);
    };

    const handleModalSuccess = (productLine: ProductLineForm) => {
        // Invalidate the product lines cache to refresh the list
        queryClient.invalidateQueries({ queryKey: ["allProductLines"] });

        // Show success message
        snackbar.setSnackbarProps({
            message: editingProductLine ? "Product line updated successfully" : "Product line created successfully",
            type: "success",
        });
    };

    // Filter product lines based on search term
    const allFilteredProductLines =
        productLinesForm?.filter((productLine) => productLine.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        [];

    // Get only root product lines for initial display, then flatten for hierarchical display
    const filteredRootProductLines = getRootProductLines(allFilteredProductLines);

    // Get product lines for hierarchical display
    const displayProductLines = flattenProductLinesForDisplay(filteredRootProductLines);

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Box sx={{ marginLeft: "16px" }}>
                        <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                            {t("gammes", "Product Lines")}
                        </Typography>
                    </Box>
                </Box>

                {/* Search and Add Button Section */}
                <Box
                    sx={{
                        px: 4,
                        py: 3,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    {/* Search Field */}
                    <TextField
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16, color: "#393A36" }} />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: "24px",
                                backgroundColor: "white",
                                border: "1px solid #E7E6E4",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "#A3A2A0",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                height: "40px",
                                pl: 2.5,
                                pr: 1.5,
                            },
                        }}
                        sx={{ width: 323 }}
                    />

                    {/* Add Product Line Button */}
                    {canUser(CREATE, PRODUCT_LINE) && (
                        <Button
                            variant="contained"
                            startIcon={<FoodsIcon size={2} icon="plus" />}
                            onClick={handleOpenCreateModal}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    color: "white !important",
                                },
                                "&:disabled": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    opacity: 0.36,
                                    color: "white !important",
                                },
                            })}
                        >
                            {t("ajouter_une_gamme", "Add a product line")}
                        </Button>
                    )}
                </Box>

                {/* Content area */}
                <Box sx={{ padding: "0px 32px 24px" }}>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: "1px solid #E7E6E4",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: "#FEFCFB",
                                        "& .MuiTableCell-root": {
                                            borderBottom: "1px solid #E7E6E4",
                                            borderRight: "1px solid #E7E6E4",
                                            padding: "0px 14px 0px 20px",
                                            height: "48px",
                                            "&:last-child": {
                                                borderRight: "none",
                                            },
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("nom_de_la_gamme", "Product Line Name")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("actions", "Actions")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayProductLines.length === 0 ?
                                    <TableRow>
                                        <TableCell colSpan={2} sx={{ textAlign: "center", py: 4 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.875rem",
                                                    color: "#767673",
                                                }}
                                            >
                                                {searchTerm ?
                                                    t(
                                                        "productLine.noSearchResults",
                                                        "No product lines found matching your search.",
                                                    )
                                                :   t(
                                                        "productLine.noProductLines",
                                                        "No product lines available. Create your first product line to get started.",
                                                    )
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                :   displayProductLines.map((productLine, index) => (
                                        <TableRow
                                            key={productLine.id}
                                            sx={{
                                                borderBottom: "1px solid #E7E6E4",
                                                "&:last-child": {
                                                    borderBottom: "none",
                                                },
                                                "& .MuiTableCell-root": {
                                                    borderRight: "1px solid #E7E6E4",
                                                    padding: "0px 14px 0px 20px",
                                                    height: "58px",
                                                    "&:last-child": {
                                                        borderRight: "none",
                                                        padding: "0px 8px",
                                                    },
                                                },
                                                "&:hover": {
                                                    backgroundColor: "#F9F9F8",
                                                },
                                            }}
                                        >
                                            {/* Product Line Name */}
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        paddingLeft: `${(productLine.level || 0) * 24}px`,
                                                    }}
                                                >
                                                    {
                                                        hasChildren(productLine.id!) ?
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleRowExpansion(productLine.id!)}
                                                                sx={{
                                                                    p: 0.5,
                                                                    backgroundColor: "transparent",
                                                                    "&:hover": {
                                                                        backgroundColor: "transparent",
                                                                    },
                                                                    "&:active": {
                                                                        backgroundColor: "transparent",
                                                                    },
                                                                    "&:focus": {
                                                                        backgroundColor: "transparent",
                                                                    },
                                                                }}
                                                            >
                                                                {expandedRows.has(productLine.id!) ?
                                                                    <KeyboardArrowDownIcon
                                                                        sx={{ fontSize: 16, color: "#393A36" }}
                                                                    />
                                                                :   <KeyboardArrowRightIcon
                                                                        sx={{ fontSize: 16, color: "#393A36" }}
                                                                    />
                                                                }
                                                            </IconButton>
                                                            // Invisible spacer to maintain alignment for product lines without children
                                                        :   <Box sx={{ width: 24, height: 24 }} />
                                                    }
                                                    <Typography
                                                        variant="small-medium"
                                                        sx={{
                                                            lineHeight: "22px",
                                                            color: "#1E1E1E",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {productLine.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    {productLine.canModify && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleOpenEditModal(productLine)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="edit" size={1} />
                                                        </Box>
                                                    )}
                                                    {productLine.canDelete && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleDeleteClick(productLine)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="delete" size={1} />
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            <DeleteProductLineDialog
                open={deleteDialog.open}
                step={deleteDialog.step}
                productLineName={deleteDialog.productLineName}
                impact={deleteDialog.impact}
                isDeleting={deleteProductLine.isLoading}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />

            {/* Product Line Modal */}
            <ProductLineModal
                open={isModalOpen}
                onClose={handleCloseModal}
                productLine={editingProductLine}
                onSuccess={handleModalSuccess}
            />
        </Box>
    );
};
